import { useMemo, useState } from 'react'
import {
  DataSurface,
  PageDateFilterBar,
  StatCard,
  StatGrid,
  TableCard,
  Td,
  Thead,
  Tr,
  GamesPanel,
} from '../../../shared/components'
import { ChildrenIcon, InfoIcon, PlayIcon, SchoolIcon } from '../../../shared/ui/icons'
import { useResource } from '../../../shared/hooks/useResource'
import { getPlatformGames, type PlatformGames } from '../../../shared/api/games'
import {
  getEngagement,
  getPlatformGrowth,
  getPlatformOverview,
  type EngagementOverview,
  type GrowthSeries,
  type PlatformOverview,
} from '../../../shared/api/platform'
import { GAMES_PANEL_COPY } from '../../../shared/ui/gamesCopy'
import { formatPercent } from '../../../shared/lib/format'
import type { StaffAuth } from '../../../shared/api/teaching'
import type { Lang } from '../../../shared/i18n'
import { describePlatformError } from './copy'
import { foldCensus, tilePair, tileValue } from './census'
import { GrowthChart } from './GrowthChart'

/**
 * *"Is anybody actually using this product?"* — the operator's engagement screen.
 *
 * 🔴 IT TOOK NO `auth` AND MADE NO REQUEST. The component signature was
 * `PlatformEngagementScreen({}: Props)` — every prop discarded — and the six
 * hundred lines under it were a static mockup. What it drew was not empty; it
 * was confident:
 *
 *   · *"Sessions by Platform"* listed **Android Tablet 46.5% (58,278) · Windows
 *     PC 24.3% (30,485) · Android Mobile 18.7% (23,498) · iOS Tablet 7.2%
 *     (9,026)** — a hundred and twenty-five thousand sittings, on a platform
 *     with a hundred and forty-nine. `Device` carries a free-text `label` and no
 *     platform column at all, so no query could have produced that split.
 *   · *"Engagement by Time of Day"* was a fully-shaded seven-by-six heatmap.
 *     Nothing anywhere buckets events by hour.
 *   · *"Quick Insights"* announced that **G8 Lily Pad Count** was the most
 *     played game — a slug retired before this file was written (`sky-quest`
 *     now) — and that **28,765 students** were active in the last seven days.
 *   · The trend chart's axis read **Apr 20 – May 18** whatever the date, over an
 *     SVG containing a gradient definition and no path.
 *
 * ⚠️ THE REPLACEMENT IS NOT A SMALLER MOCKUP. Every panel below is a live read,
 * and the panels whose questions nothing on this platform can answer — device
 * class, hour-of-day, assignments, "average session duration" — are gone rather
 * than dashed. `DataSurface`'s rule, applied at the level of a whole card: a
 * card that can never fill is not an empty state, it is a promise.
 *
 * 🔴 AND THE PER-GAME TABLE IS `GamesPanel`, THE ONE FOUR TIERS SHARE. A
 * hand-built "Top Games by Plays" here is exactly the second vocabulary
 * `architecture.md` §12 forbids — it is how a platform total stops matching the
 * class panels inside it.
 */

export interface PlatformEngagementScreenProps {
  lang?: Lang
  auth?: StaffAuth
}

/**
 * Whole days between two `yyyy-mm-dd` strings, at least one.
 *
 * ⚠️ AT LEAST ONE, because "today to today" is a real thing to ask for and a
 * window of zero days would return an empty series that reads as "nobody".
 */
export function daysBetween(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00.000Z`).getTime()
  const b = new Date(`${to}T00:00:00.000Z`).getTime()
  if (Number.isNaN(a) || Number.isNaN(b)) return 30
  return Math.max(1, Math.round((b - a) / 86_400_000))
}

export function PlatformEngagementScreen({ lang = 'en', auth }: PlatformEngagementScreenProps) {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().split('T')[0]
  })
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0])

  /*
   * 🔴 THE DATE BAR NOW CHANGES A REQUEST. It sat above six static cards and
   * moved nothing — a control that appears to filter and does not is worse than
   * no control, because a reader takes the figures below it as filtered.
   *
   * ⚠️ IT DRIVES THE GROWTH WINDOW ONLY, AND THE CAPTIONS SAY SO. The overview,
   * games and engagement reads each carry their own server-chosen window
   * (`windowDays` travels with every one of them, deliberately); pretending a
   * date picker had narrowed those would be the same lie one layer down.
   */
  const growthDays = useMemo(() => daysBetween(fromDate, toDate), [fromDate, toDate])

  const overviewResource = useResource<PlatformOverview>(
    (signal) => getPlatformOverview({ ...auth, signal }),
    ['platform-overview'],
  )
  const engagementResource = useResource<EngagementOverview>(
    (signal) => getEngagement({ ...auth, signal }),
    ['platform-engagement'],
  )
  const gamesResource = useResource<PlatformGames>(
    (signal) => getPlatformGames({ ...auth, signal }),
    ['platform-games'],
  )
  const growthResource = useResource<GrowthSeries>(
    (signal) => getPlatformGrowth(growthDays, { ...auth, signal }),
    ['platform-growth', growthDays],
  )

  const overview = overviewResource.data
  const engagement = engagementResource.data
  const games = gamesResource.data

  const census = useMemo(
    () => foldCensus(overview, engagement, null, growthResource.data),
    [overview, engagement, growthResource.data],
  )

  /*
   * ⚠️ WORST FIRST, AND ONLY SCHOOLS WITH SEATS. A school with nobody enrolled
   * has nothing to be engaged about; ranking it at 0% would bury the school that
   * enrolled thirty children and lost them. The server sets `engagement` to
   * `null` there for exactly that reason, so this filters those out rather than
   * reading the null as a zero.
   */
  const schoolsByEngagement = useMemo(() => {
    const rows = (overview?.perSchool ?? []).filter((s) => s.enrolledStudents > 0)
    return [...rows].sort((a, b) => (a.engagement ?? 101) - (b.engagement ?? 101))
  }, [overview])

  const t = GAMES_PANEL_COPY[lang]

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      <div>
        <h1 className="font-dash text-dash-2xl font-extrabold text-ink">Analytics &amp; Reports</h1>
        <p className="mt-1 font-dash text-dash-sm text-muted">
          What the platform is actually being used for — every figure read from the
          events and sign-ins behind it.
        </p>
      </div>

      <PageDateFilterBar
        title="Growth window"
        fromDate={fromDate}
        toDate={toDate}
        onApply={(from, to) => {
          setFromDate(from)
          setToDate(to)
        }}
      />

      {/*
        🔴 SIX TILES, SIX READS. They were six hardcoded em dashes under six
        hardcoded "Not measured yet" captions — including "Assignments Completed"
        and "Avg. Session Duration", two figures this product does not record at
        all. Those two are gone rather than dashed; the four that were measurable
        are wired, and two the same responses already carried have taken their
        place.
      */}
      <StatGrid>
        <StatCard
          label="Children who played"
          icon={<ChildrenIcon size={18} />}
          value={tilePair(census.activeStudents, census.students)}
          caption={
            census.activeWindowDays === null
              ? undefined
              : `in the last ${String(census.activeWindowDays)} days`
          }
          tone="primary"
        />
        <StatCard
          label="Rounds finished"
          icon={<InfoIcon size={18} />}
          value={tileValue(games?.summary.rounds ?? null)}
          caption={games === null ? undefined : `in the last ${String(games.windowDays)} days`}
          tone="indigo"
        />
        {/* ⚠️ `null` ACCURACY IS A DASH, NOT 0% — nobody has answered anything yet,
            which is the opposite of everybody getting them wrong. */}
        <StatCard
          label="Right answers"
          icon={<InfoIcon size={18} />}
          value={
            games === null || games.summary.accuracyPct === null
              ? '—'
              : formatPercent(games.summary.accuracyPct)
          }
          tone="teal"
        />
        <StatCard
          label="Games started"
          icon={<PlayIcon size={18} />}
          value={
            games === null
              ? '—'
              : `${String(games.summary.gamesPlayed)} of ${String(games.summary.gamesAvailable)}`
          }
          tone="leaf"
        />
        <StatCard
          label="Teachers signed in"
          icon={<SchoolIcon size={18} />}
          value={tilePair(census.activeTeachers, census.teachers)}
          caption={
            census.engagementWindowDays === null
              ? undefined
              : `in the last ${String(census.engagementWindowDays)} days`
          }
          tone="indigo"
        />
        <StatCard
          label="Parents signed in"
          icon={<ChildrenIcon size={18} />}
          value={tilePair(census.activeParents, census.parents)}
          caption={
            census.engagementWindowDays === null
              ? undefined
              : `in the last ${String(census.engagementWindowDays)} days`
          }
          tone="primary"
        />
      </StatGrid>

      {census.sampled ? (
        <p className="text-dash-xs font-semibold text-muted">
          The engagement read hit its cap — the teacher and parent figures above are a
          sample rather than a census.
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card sm:p-6">
          <h3 className="font-dash text-dash-md font-extrabold text-ink">Growth</h3>
          {/* ⚠️ THE COUNTING RULE COMES FROM THE SERVER, printed rather than
              restated — a second copy of the sentence is a second copy to drift. */}
          <p className="mb-3 text-dash-xs font-medium text-muted">
            {growthResource.data === null
              ? `Students, teachers and parents over ${String(growthDays)} days.`
              : growthResource.data.basis}
          </p>
          <DataSurface
            state={growthResource.state}
            skeletonRows={4}
            errorMessage={describePlatformError(growthResource.error, lang)}
            errorRetryLabel="Try again"
            onRetry={growthResource.retry}
          >
            {growthResource.data === null ? null : <GrowthChart series={growthResource.data} />}
          </DataSurface>
        </div>

        {/*
          🔴 THIS CARD REPLACES "TOP INSTITUTIONS BY ENGAGEMENT", WHICH WAS A
          LEAGUE TABLE OF INVENTED SCHOOLS RANKED BY INVENTED SESSION COUNTS.
          `perSchool` has carried the real thing since F1 shipped — and it is
          sorted worst first, because the school an operator needs to ring is the
          one at the bottom of a league table, never the one at the top.
        */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card sm:p-6">
          <h3 className="font-dash text-dash-md font-extrabold text-ink">
            Schools that need a look
          </h3>
          <p className="mb-3 text-dash-xs font-medium text-muted">
            Children who played, against children enrolled. Worst first. A school with
            no seats is not listed — there is nothing to measure yet.
          </p>
          <DataSurface
            state={overviewResource.state}
            skeletonRows={4}
            errorMessage={describePlatformError(overviewResource.error, lang)}
            errorRetryLabel="Try again"
            onRetry={overviewResource.retry}
          >
            {overview === null ? null : schoolsByEngagement.length === 0 ? (
              <p className="py-6 text-center text-dash-xs font-semibold text-muted">
                No school has a child enrolled yet.
              </p>
            ) : (
              <ul className="space-y-3 text-dash-xs">
                {schoolsByEngagement.map((school) => (
                  <li key={school.schoolId}>
                    <div className="mb-1 flex items-center justify-between gap-3 font-bold text-ink">
                      <span className="truncate">{school.name}</span>
                      {/* 🔴 A PAIR, NEVER A BARE PERCENTAGE — OD-39/OD-72. */}
                      <span className="tabular shrink-0">
                        {tilePair(school.activeStudents, school.enrolledStudents)}
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-emerald-500"
                        style={{
                          width: `${String(
                            Math.min(
                              100,
                              Math.round(
                                (school.activeStudents / Math.max(1, school.enrolledStudents)) * 100,
                              ),
                            ),
                          )}%`,
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </DataSurface>
        </div>
      </div>

      {/*
        🔴 THE SAME PANEL THE CLASS, SCHOOL AND PARENT TIERS DRAW. "Top Games by
        Plays" was hand-built here; the shared one is computed by
        `GameStatsService` from the same rows every other tier reads, so a
        platform total cannot disagree with the class panels inside it.
      */}
      <DataSurface
        state={gamesResource.state}
        skeletonRows={5}
        errorMessage={describePlatformError(gamesResource.error, lang)}
        errorRetryLabel="Try again"
        onRetry={gamesResource.retry}
      >
        {games === null ? null : (
          <GamesPanel
            games={games.games}
            summary={games.summary}
            lang={lang}
            /* ⚠️ `unplayedRows={false}` — the platform tier's own rule, already
               argued inside `GamesPanel`: across every school, a row of zeroes
               beside four real rows does not read as a finding, it reads as
               sample data somebody left in. The untouched games are still named
               under the table. */
            unplayedRows={false}
            copy={{
              ...t,
              title: 'Every game, across every school',
              hint: `Rounds, right answers and the level reached, over the last ${String(
                games.windowDays,
              )} days.`,
            }}
          />
        )}
      </DataSurface>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <h3 className="mb-1 font-dash text-dash-md font-extrabold text-ink">
            Teachers running the most sittings
          </h3>
          <p className="mb-3 text-dash-xs font-medium text-muted">
            Ranked by sittings run, never by logins — a teacher who signs in daily and
            never starts a session is not using the product.
          </p>
          <DataSurface
            state={engagementResource.state}
            skeletonRows={4}
            errorMessage={describePlatformError(engagementResource.error, lang)}
            errorRetryLabel="Try again"
            onRetry={engagementResource.retry}
          >
            {engagement === null ? null : engagement.mostEngagedTeachers.length === 0 ? (
              <p className="rounded-2xl border border-line bg-surface p-6 text-center text-dash-xs font-semibold text-muted">
                No teacher has run a sitting yet.
              </p>
            ) : (
              <TableCard>
                <table className="w-full border-collapse">
                  <Thead headers={['Teacher', 'School', 'Sittings', 'Children']} />
                  <tbody>
                    {engagement.mostEngagedTeachers.map((teacher) => (
                      <Tr key={teacher.userId}>
                        <Td bold>{teacher.name ?? '—'}</Td>
                        <Td soft>{teacher.schoolName ?? '—'}</Td>
                        <Td num>{teacher.sessions}</Td>
                        <Td num>{teacher.children}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </TableCard>
            )}
          </DataSurface>
        </div>

        <div>
          <h3 className="mb-1 font-dash text-dash-md font-extrabold text-ink">
            Teachers who have never signed in
          </h3>
          {/* 🔴 A LIST, NOT A COUNT — `EngagementOverview` says why in writing:
              *"a number leaves an operator with nobody to ring."* */}
          <p className="mb-3 text-dash-xs font-medium text-muted">
            An account exists and has never been used. These are phone calls, not a
            statistic.
          </p>
          <DataSurface
            state={engagementResource.state}
            skeletonRows={4}
            errorMessage={describePlatformError(engagementResource.error, lang)}
            errorRetryLabel="Try again"
            onRetry={engagementResource.retry}
          >
            {engagement === null ? null : engagement.neverSignedInTeachers.length === 0 ? (
              <p className="rounded-2xl border border-line bg-surface p-6 text-center text-dash-xs font-semibold text-muted">
                Every teacher account has been signed into.
              </p>
            ) : (
              <TableCard>
                <table className="w-full border-collapse">
                  <Thead headers={['Teacher', 'School']} />
                  <tbody>
                    {engagement.neverSignedInTeachers.map((teacher) => (
                      <Tr key={teacher.userId}>
                        <Td bold>{teacher.name ?? '—'}</Td>
                        <Td soft>{teacher.schoolName ?? '—'}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </TableCard>
            )}
          </DataSurface>
        </div>
      </div>

      <div className="flex flex-col items-center justify-between gap-2 border-t border-line pt-2 text-[11px] font-bold text-muted sm:flex-row">
        <span>ⓘ All times are shown in Asia/Kolkata (IST)</span>
        {/*
          ⚠️ WHEN THIS WAS READ, NOT A REFRESH CADENCE. The footer claimed "Data
          is updated every 30 minutes"; nothing on this screen polls, and every
          figure is exactly as fresh as the last load.
        */}
        <span>
          {overview === null
            ? 'Loading…'
            : `Read at ${new Date(overview.generatedAt).toLocaleTimeString()}`}
        </span>
      </div>
    </section>
  )
}
